import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Collapse,
  Drawer,
  IconButton,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckIcon from '@mui/icons-material/Check';
import ReplayIcon from '@mui/icons-material/Replay';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import BlockIcon from '@mui/icons-material/Block';
import GavelIcon from '@mui/icons-material/Gavel';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { apiErrorMessage, launchApi } from './api';
import {
  displayText,
  eventDetail,
  eventTypeText,
  saveDownloadedFile,
  formatDate,
  isoWeek,
  makeId,
  primaryActivityAction,
  relativeDue,
  shortDate,
  today,
} from './helpers';
import { fieldHelp, help } from './helpTexts';
import { launchColors, overlineSx, scheduleStatusStyle } from './launchTheme';
import { ActivityAction, ActivityDetail, Assignee, LaunchFile, Workspace } from './types';
import AttachmentList from './ui/AttachmentList';
import UploadFilesDialog from './ui/UploadFilesDialog';
import DepartmentBadge from './ui/DepartmentBadge';
import PhaseChip from './ui/PhaseChip';
import InfoTip from './ui/InfoTip';
import { ScheduleStatusPill, StatusPill, WorkStatusPill } from './ui/StatusPill';

type DrawerAction = ActivityAction | 'forecast' | 'replan' | 'scope';

interface Props {
  launchId: string | null;
  activityId: string | null;
  currentUserId: string;
  assignees: Assignee[];
  canApprove: boolean;
  canAdminAll: boolean;
  onClose: () => void;
  onChanged: () => void;
}

const actionText: Record<ActivityAction, string> = {
  start: 'Starta',
  assign: 'Tilldela',
  complete: 'Markera klar',
  reopen: 'Återöppna',
  blocker: 'Registrera hinder',
  resolveBlocker: 'Lös hinder',
  decide: 'Registrera beslut',
  revise: 'Ny version av underlaget',
};

const ActivityDetailDrawer: React.FC<Props> = ({
  launchId,
  activityId,
  currentUserId,
  assignees,
  canApprove,
  canAdminAll,
  onClose,
  onChanged,
}) => {
  const [detail, setDetail] = useState<ActivityDetail | null>(null);
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [action, setAction] = useState<DrawerAction | null>(null);
  const [planOpen, setPlanOpen] = useState(false);
  const [technicalOpen, setTechnicalOpen] = useState(false);
  const [comment, setComment] = useState('');
  const [assigneeUserId, setAssigneeUserId] = useState('');
  const [role, setRole] = useState('');
  const [decision, setDecision] = useState<'approved' | 'rejected'>('approved');
  const [revision, setRevision] = useState('');
  const [evidenceNote, setEvidenceNote] = useState('');
  const [evidenceUrl, setEvidenceUrl] = useState('');
  const [forecastDue, setForecastDue] = useState('');
  const [forecastSource, setForecastSource] = useState('');
  const [planReason, setPlanReason] = useState('');
  const [planDates, setPlanDates] = useState<Record<string, string>>({});
  const [occurredAt, setOccurredAt] = useState(today());
  const [saving, setSaving] = useState(false);
  const [fileBusy, setFileBusy] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!launchId || !activityId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const [nextDetail, nextWorkspace] = await Promise.all([
        launchApi.activity(launchId, activityId),
        launchApi.workspace(launchId),
      ]);
      setDetail(nextDetail);
      setWorkspace(nextWorkspace);
      setAssigneeUserId(nextDetail.activity.assigneeUserId || '');
      setRevision(nextDetail.source.evidenceRevision);
      setForecastDue(nextDetail.activity.forecastDue || '');
      setForecastSource(nextDetail.activity.forecastSource || '');
      setPlanDates(Object.fromEntries(nextDetail.chainRequirements.map((item) => [item.windowId, item.due || ''])));
    } catch (error) {
      setLoadError(apiErrorMessage(error, 'Aktiviteten kunde inte hämtas.'));
    } finally {
      setLoading(false);
    }
  }, [activityId, launchId]);

  useEffect(() => {
    setDetail(null);
    setWorkspace(null);
    setAction(null);
    setActionError(null);
    setPlanOpen(false);
    setTechnicalOpen(false);
    setUploadOpen(false);
    setUploadError(null);
    if (launchId && activityId) void load();
  }, [activityId, launchId, load]);

  const uploadAttachments = async (files: File[], folderId: string) => {
    if (!launchId || !activityId) return;
    setFileBusy(true);
    setUploadError(null);
    setUploadProgress(0);
    try {
      await launchApi.uploadFiles(launchId, { commandId: makeId('files'), folderId, activityId, files }, setUploadProgress);
      setUploadOpen(false);
      setNotice('Filerna har laddats upp.');
      await load();
      onChanged();
    } catch (error) {
      setUploadError(apiErrorMessage(error, 'Filerna kunde inte laddas upp.'));
    } finally {
      setFileBusy(false);
      setUploadProgress(null);
    }
  };

  const downloadAttachment = async (file: LaunchFile) => {
    if (!launchId) return;
    try {
      const blob = await launchApi.downloadFile(launchId, file.id);
      saveDownloadedFile(blob, file.fileName);
    } catch (error) {
      setActionError(apiErrorMessage(error, 'Filen kunde inte laddas ner.'));
    }
  };

  const changeAttachment = async (file: LaunchFile, type: 'removeFile' | 'moveFile', folderId?: string) => {
    if (!launchId) return;
    setFileBusy(true);
    setActionError(null);
    try {
      await launchApi.fileCommand(launchId, {
        commandId: makeId(type),
        type,
        attachmentId: file.id,
        ...(folderId ? { folderId } : {}),
      });
      setNotice(type === 'removeFile' ? 'Filen har tagits bort.' : 'Filen har flyttats.');
      await load();
      onChanged();
    } catch (error) {
      setActionError(apiErrorMessage(error, 'Filen kunde inte ändras.'));
    } finally {
      setFileBusy(false);
    }
  };

  const conflictReload = async () => {
    setNotice('Någon annan hann ändra lanseringen. Uppgifterna har laddats om.');
    setActionError('Kontrollera de nya uppgifterna och försök igen.');
    await load();
    onChanged();
  };

  const submitActivity = async () => {
    if (!launchId || !activityId || !workspace || !action || action === 'forecast' || action === 'replan' || action === 'scope' || !detail) return;
    setSaving(true);
    setActionError(null);
    const command: Record<string, unknown> = {
      commandId: makeId(action),
      type: action,
      expectedVersion: workspace.expectedVersion,
      actuallyOccurredAt: occurredAt,
    };
    if (action === 'assign') command.assigneeUserId = assigneeUserId;
    if (['reopen', 'blocker', 'resolveBlocker'].includes(action)) command.comment = comment.trim();
    if (action === 'decide') {
      command.role = role;
      command.decision = decision;
      command.evidenceRevision = detail.source.evidenceRevision;
    }
    if (action === 'revise') {
      command.evidenceRevision = revision.trim();
      command.evidenceNote = evidenceNote.trim();
      command.evidenceUrl = evidenceUrl.trim();
    }
    try {
      await launchApi.activityCommand(launchId, activityId, command);
      setAction(null);
      setComment('');
      await load();
      onChanged();
      setNotice('Aktiviteten har uppdaterats.');
    } catch (error: any) {
      if (error?.response?.status === 409) {
        await conflictReload();
      } else {
        setActionError(apiErrorMessage(error, 'Ändringen kunde inte sparas.'));
      }
    } finally {
      setSaving(false);
    }
  };

  const submitForecast = async () => {
    if (!launchId || !activityId || !workspace) return;
    setSaving(true);
    setActionError(null);
    try {
      await launchApi.launchCommand(launchId, {
        commandId: makeId('forecast'),
        type: 'setForecast',
        expectedVersion: workspace.expectedVersion,
        actuallyOccurredAt: occurredAt,
        activityId,
        forecastDue: forecastDue || null,
        source: forecastSource.trim(),
        assessedAt: occurredAt,
      });
      setAction(null);
      await load();
      onChanged();
      setNotice('Det väntade datumet är sparat.');
    } catch (error: any) {
      if (error?.response?.status === 409) await conflictReload();
      else setActionError(apiErrorMessage(error, 'Det väntade datumet kunde inte sparas.'));
    } finally {
      setSaving(false);
    }
  };

  const submitReplan = async () => {
    if (!launchId || !activityId || !workspace || !detail) return;
    setSaving(true);
    setActionError(null);
    try {
      await launchApi.launchCommand(launchId, {
        commandId: makeId('plan-proposal'),
        type: 'proposePlanChange',
        expectedVersion: workspace.expectedVersion,
        actuallyOccurredAt: occurredAt,
        proposalId: makeId('plan'),
        kind: 'chain_plan',
        reason: planReason.trim(),
        changes: {
          chainRequirements: {
            [activityId]: detail.chainRequirements.map((requirement) => ({
              ...requirement,
              due: planDates[requirement.windowId] || null,
            })),
          },
        },
      });
      setAction(null);
      setPlanReason('');
      await load();
      onChanged();
      setNotice('Datumförslaget är sparat och väntar på godkännande under Historik.');
    } catch (error: any) {
      if (error?.response?.status === 409) await conflictReload();
      else setActionError(apiErrorMessage(error, 'Planförslaget kunde inte sparas.'));
    } finally {
      setSaving(false);
    }
  };

  const submitScopeChange = async () => {
    if (!launchId || !activityId || !workspace || !detail) return;
    setSaving(true);
    setActionError(null);
    try {
      await launchApi.launchCommand(launchId, {
        commandId: makeId('scope-proposal'),
        type: 'proposePlanChange',
        expectedVersion: workspace.expectedVersion,
        actuallyOccurredAt: occurredAt,
        proposalId: makeId('scope'),
        kind: 'scope',
        reason: planReason.trim(),
        changes: { activityInclusions: { [activityId]: !detail.activity.included } },
      });
      setAction(null);
      setPlanReason('');
      await load();
      onChanged();
      setNotice('Förslaget är sparat och väntar på godkännande under Historik.');
    } catch (error: any) {
      if (error?.response?.status === 409) await conflictReload();
      else setActionError(apiErrorMessage(error, 'Omfattningsförslaget kunde inte sparas.'));
    } finally {
      setSaving(false);
    }
  };

  const readOnly = Boolean(workspace && ['closed', 'cancelled'].includes(workspace.launch.status));
  const canForecast = Boolean(workspace && !readOnly && (canAdminAll || workspace.launch.ownerUserId === currentUserId));
  const evidenceLink = safeHttpUrl(detail?.source.evidenceUrl);
  const asOf = workspace?.timeline.asOf || today();
  const chooseAction = (next: DrawerAction) => {
    setAction(next);
    setActionError(null);
    if (next === 'decide' && detail) setRole(detail.source.requiredRoles[0] || '');
  };
  const allowed = detail ? detail.activity.allowedActions.filter((item) => item !== 'decide' || canApprove) : [];
  const primary = detail ? primaryActivityAction(detail.activity, canApprove) : null;
  const secondary = allowed.filter((item) => item !== primary && item !== 'assign' && item !== 'revise');
  const textActions = action && primary ? [primary, ...secondary] : secondary;
  const ownerActions: Array<{ key: DrawerAction; label: string }> = detail ? [
    ...(canForecast ? [{ key: 'forecast' as const, label: 'Ange väntat datum' }] : []),
    ...(canForecast && detail.chainRequirements.length > 0 ? [{ key: 'replan' as const, label: 'Föreslå nytt datum' }] : []),
    ...(canForecast && detail.activity.kind !== 'calendar_marker'
      ? [{ key: 'scope' as const, label: detail.activity.included ? 'Ta bort från planen' : 'Lägg till i planen' }]
      : []),
    ...(allowed.includes('revise') ? [{ key: 'revise' as const, label: 'Ny version av underlaget' }] : []),
  ] : [];
  const dateStyle = detail ? scheduleStatusStyle[detail.activity.scheduleStatus] : null;
  const submitLabel = action && action in actionText ? actionText[action as ActivityAction] : 'Spara';

  return (
    <>
      <Drawer anchor="right" open={Boolean(launchId && activityId)} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 560 }, maxWidth: '100%', bgcolor: launchColors.card } }}>
        <Box sx={{ p: 2.25, pb: 1.75, borderBottom: `1px solid ${launchColors.divider}` }}>
          <Stack direction="row" alignItems="flex-start" spacing={1}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {detail && <PhaseChip phaseKey={detail.activity.phaseKey} name={detail.activity.phaseName} size="medium" />}
              <Typography component="h2" sx={{ fontSize: 19, fontWeight: 700, color: launchColors.navy, lineHeight: 1.3, mt: 0.5, overflowWrap: 'anywhere' }}>
                {detail ? displayText(detail.activity.title) : 'Aktivitet'}
              </Typography>
            </Box>
            <IconButton aria-label="Stäng aktivitetsdetaljer" onClick={onClose}><CloseIcon /></IconButton>
          </Stack>
          {detail && (
            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1.25 }}>
              <WorkStatusPill status={detail.activity.workStatus} />
              <ScheduleStatusPill status={detail.activity.scheduleStatus} />
              {detail.activity.activeBlocker && <StatusPill label="Blockerad" fg={launchColors.dangerText} bg={launchColors.dangerBg} />}
              {detail.activity.kind === 'calendar_marker' && <StatusPill label="Hållpunkt" fg={launchColors.petrolDark} bg={launchColors.petrolLight} />}
              {!detail.activity.included && <StatusPill label="Utanför aktuell omfattning" fg={launchColors.textSecondary} bg={launchColors.neutralBg} />}
            </Stack>
          )}
        </Box>
        <Box sx={{ p: 2.25, overflowY: 'auto' }}>
          {loading && <Typography>Laddar aktivitet…</Typography>}
          {loadError && <Alert severity="error">{loadError}</Alert>}
          {detail && workspace && dateStyle && (
            <Stack spacing={2.5}>
              {readOnly && (
                <Alert severity="info">Lanseringen är {workspace.launch.status === 'cancelled' ? 'avbruten' : 'avslutad'}. Aktiviteten är skrivskyddad.</Alert>
              )}
              {workspace.launch.status === 'paused' && (
                <Alert severity="warning">Lanseringen är pausad. Arbetet kan fortfarande registreras.</Alert>
              )}

              <Box sx={{ bgcolor: dateStyle.bg, border: dateStyle.border || `1px solid ${dateStyle.bg}`, borderRadius: '12px', p: 1.75 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                  <Box>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: dateStyle.fg }}>Deadline</Typography>
                      <InfoTip text={help.drawerDate} />
                    </Stack>
                    <Typography sx={{ fontSize: 22, fontWeight: 700, color: dateStyle.fg, fontVariantNumeric: 'tabular-nums' }}>
                      {detail.activity.due ? formatDate(detail.activity.due) : 'Datum saknas'}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: 15, fontWeight: 700, color: dateStyle.fg, textAlign: 'right' }}>
                    {detail.activity.scheduleStatus === 'complete'
                      ? 'Klar'
                      : detail.activity.due ? relativeDue(detail.activity.due, asOf) : 'Inget datum'}
                  </Typography>
                </Stack>
                {detail.activity.forecastDue && (
                  <Typography sx={{ fontSize: 13, color: dateStyle.fg, mt: 0.75 }}>Väntat datum: {formatDate(detail.activity.forecastDue)}</Typography>
                )}
              </Box>

              {detail.activity.activeBlocker && (
                <Alert severity="error">Hinder: {detail.activity.activeBlocker.comment} ({formatDate(detail.activity.activeBlocker.actuallyOccurredAt)})</Alert>
              )}

              <DepartmentBadge name={detail.activity.roleText} size={36} />

              {(primary || secondary.length > 0) && (
                <Box>
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.75 }}>
                    <Typography component="h3" sx={overlineSx}>Handlingar</Typography>
                    <InfoTip text={help.drawerActions} />
                  </Stack>
                  {primary && !action && (
                    <Button fullWidth size="large" variant="contained" onClick={() => chooseAction(primary)} sx={{ height: 44, fontSize: 15 }}>
                      {actionText[primary]}
                    </Button>
                  )}
                  {textActions.length > 0 && (
                    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: primary && !action ? 1 : 0 }}>
                      {textActions.map((item) => (
                        <Button key={item} size="small" variant="text" onClick={() => chooseAction(item)} aria-pressed={action === item}>
                          {actionText[item]}
                        </Button>
                      ))}
                    </Stack>
                  )}
                </Box>
              )}
              {!readOnly && !primary && secondary.length === 0 && ownerActions.length === 0 && (
                <Typography sx={{ fontSize: 13, color: launchColors.textSecondary }}>Du kan följa aktiviteten men har inga handlingar här.</Typography>
              )}

              {ownerActions.length > 0 && (
                <Box sx={{ border: `1px solid ${launchColors.divider}`, borderRadius: '12px', p: 1.5 }}>
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
                    <Typography component="h3" sx={overlineSx}>Ägaråtgärder</Typography>
                    <InfoTip text={help.drawerOwner} />
                  </Stack>
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    {ownerActions.map((item) => (
                      <Button
                        key={item.key}
                        size="small"
                        variant="outlined"
                        aria-pressed={action === item.key}
                        onClick={() => chooseAction(item.key)}
                        sx={action === item.key ? { bgcolor: launchColors.softNavy } : undefined}
                      >
                        {item.label}
                      </Button>
                    ))}
                  </Stack>
                </Box>
              )}

              {action && (
                <Stack spacing={1.5} sx={{ p: 2, bgcolor: launchColors.section, border: `1px solid ${launchColors.divider}`, borderRadius: '12px' }}>
                  <TextField type="date" label="Faktiskt händelsedatum" helperText={fieldHelp.occurredAt} value={occurredAt} InputLabelProps={{ shrink: true }} onChange={(event) => setOccurredAt(event.target.value)} />
                  {action === 'assign' && (
                    <TextField required select label="Tilldela person" value={assigneeUserId} onChange={(event) => setAssigneeUserId(event.target.value)}>
                      {assignees.map((person) => <MenuItem key={person.id} value={person.id}>{person.name}</MenuItem>)}
                    </TextField>
                  )}
                  {['reopen', 'blocker', 'resolveBlocker'].includes(action) && (
                    <TextField required label={action === 'blocker' ? 'Beskriv hindret' : 'Orsak/kommentar'} value={comment} multiline minRows={2} onChange={(event) => setComment(event.target.value)} />
                  )}
                  {action === 'decide' && (
                    <>
                      <TextField required select label="Beslutsroll" helperText={fieldHelp.decisionRole} value={role} onChange={(event) => setRole(event.target.value)}>
                        {detail.source.requiredRoles.map((item) => <MenuItem key={item} value={item}>{displayText(item)}</MenuItem>)}
                      </TextField>
                      <TextField select label="Beslut" value={decision} onChange={(event) => setDecision(event.target.value as 'approved' | 'rejected')}>
                        <MenuItem value="approved">Godkänn</MenuItem>
                        <MenuItem value="rejected">Avslå</MenuItem>
                      </TextField>
                    </>
                  )}
                  {action === 'revise' && (
                    <>
                      <TextField required label="Ny revisionsbeteckning" value={revision} onChange={(event) => setRevision(event.target.value)} />
                      <TextField label="Notering om underlaget" value={evidenceNote} multiline minRows={2} onChange={(event) => setEvidenceNote(event.target.value)} />
                      <TextField label="Länk till underlag" value={evidenceUrl} onChange={(event) => setEvidenceUrl(event.target.value)} />
                    </>
                  )}
                  {action === 'forecast' && (
                    <>
                      <TextField type="date" label="Väntat datum (tomt tar bort det)" value={forecastDue} InputLabelProps={{ shrink: true }} onChange={(event) => setForecastDue(event.target.value)} />
                      <TextField required label="Källa till datumet" value={forecastSource} onChange={(event) => setForecastSource(event.target.value)} />
                    </>
                  )}
                  {action === 'replan' && (
                    <>
                      <Alert severity="info">Det väntade datumet ändrar inte planen. Nya datum gäller först när förslaget godkänns under Historik.</Alert>
                      {detail.chainRequirements.map((requirement) => (
                        <TextField
                          key={requirement.windowId}
                          type="date"
                          label={`${requirement.chain}: föreslagen deadline`}
                          value={planDates[requirement.windowId] || ''}
                          InputLabelProps={{ shrink: true }}
                          onChange={(event) => setPlanDates((dates) => ({ ...dates, [requirement.windowId]: event.target.value }))}
                        />
                      ))}
                      <TextField required label="Orsak till omplanering" value={planReason} multiline minRows={2} onChange={(event) => setPlanReason(event.target.value)} />
                    </>
                  )}
                  {action === 'scope' && (
                    <>
                      <Alert severity="warning">
                        {detail.activity.included
                          ? 'Uppgiften tas bort från planen först när förslaget godkänns under Historik.'
                          : 'Uppgiften läggs till i planen först när förslaget godkänns under Historik.'}
                      </Alert>
                      <TextField required label="Orsak till ändrad omfattning" value={planReason} multiline minRows={2} onChange={(event) => setPlanReason(event.target.value)} />
                    </>
                  )}
                  {actionError && <Alert severity="error">{actionError}</Alert>}
                  <Stack direction="row" spacing={1}>
                    <Button onClick={() => setAction(null)}>Avbryt</Button>
                    <Button
                      variant="contained"
                      disabled={saving
                        || (!occurredAt)
                        || (action === 'assign' && !assigneeUserId)
                        || (['reopen', 'blocker', 'resolveBlocker'].includes(action) && !comment.trim())
                        || (action === 'decide' && !role)
                        || (action === 'revise' && !revision.trim())
                        || (action === 'forecast' && !forecastSource.trim())
                        || (['replan', 'scope'].includes(action) && !planReason.trim())}
                      onClick={action === 'forecast' ? submitForecast : action === 'replan' ? submitReplan : action === 'scope' ? submitScopeChange : submitActivity}
                    >
                      {saving ? 'Sparar…' : submitLabel}
                    </Button>
                  </Stack>
                </Stack>
              )}

              <Box>
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.75 }}>
                  <Typography component="h3" sx={overlineSx}>Klardefinition</Typography>
                  <InfoTip text={help.drawerDefinition} />
                </Stack>
                <Typography sx={{ fontSize: 14 }}>{detail.activity.approvalText || 'Följ aktivitetens aktuella beslutskrav.'}</Typography>
                {detail.source.evidenceNote && <Typography sx={{ fontSize: 13, mt: 0.5 }}>Underlag: {detail.source.evidenceNote}</Typography>}
                {evidenceLink && <Button size="small" href={evidenceLink} target="_blank" rel="noopener noreferrer" sx={{ mt: 0.5, px: 0 }}>Öppna underlag</Button>}
                {detail.decisions.length > 0 && (
                  <Stack spacing={0.5} sx={{ mt: 1 }}>
                    {detail.decisions.map((item, index) => (
                      <Stack key={`${item.role}-${item.recordedAt}-${index}`} direction="row" spacing={1} alignItems="center">
                        <StatusPill
                          label={item.decision === 'approved' ? 'Godkänd' : 'Avslagen'}
                          fg={item.decision === 'approved' ? launchColors.mossDark : launchColors.dangerText}
                          bg={item.decision === 'approved' ? launchColors.mossLight : launchColors.dangerBg}
                        />
                        <Typography sx={{ fontSize: 13 }}>{displayText(item.role)} · {formatDate(item.actuallyOccurredAt)}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </Box>

              <Box>
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.75 }}>
                  <Typography component="h3" sx={overlineSx}>Bilagor</Typography>
                  <InfoTip text={help.drawerFiles} />
                  <Box sx={{ flex: 1 }} />
                  {detail.canUpload && (
                    <Button size="small" variant="outlined" startIcon={<AttachFileOutlinedIcon />} onClick={() => { setUploadError(null); setUploadOpen(true); }}>
                      Bifoga fil
                    </Button>
                  )}
                </Stack>
                {(detail.attachments ?? []).length === 0 && (
                  <Typography sx={{ fontSize: 13, color: launchColors.textSecondary }}>Inga bilagor ännu.</Typography>
                )}
                <AttachmentList
                  files={detail.attachments ?? []}
                  folders={detail.fileFolders ?? []}
                  assignees={assignees}
                  busy={fileBusy}
                  onDownload={(file) => { void downloadAttachment(file); }}
                  onDelete={(file) => { void changeAttachment(file, 'removeFile'); }}
                  onMove={(file, folderId) => { void changeAttachment(file, 'moveFile', folderId); }}
                />
              </Box>

              {detail.chainRequirements.length > 0 && (
                <Box>
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.75 }}>
                    <Typography component="h3" sx={overlineSx}>Kedjekrav</Typography>
                    <InfoTip text={help.drawerChains} />
                  </Stack>
                  {detail.chainRequirements.map((requirement) => (
                    <Typography sx={{ fontSize: 13, py: 0.25 }} key={`${requirement.windowId}-${requirement.due}`}>
                      <strong>{requirement.chain}</strong> · deadline {shortDate(requirement.due, asOf)} · avisering {shortDate(requirement.announcement, asOf)} (v.{isoWeek(requirement.announcement)})
                    </Typography>
                  ))}
                </Box>
              )}

              <Box>
                <Stack direction="row" alignItems="center">
                <Button size="small" onClick={() => setPlanOpen((open) => !open)} aria-expanded={planOpen} endIcon={planOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />} sx={{ px: 0 }}>
                  Visa planhistorik
                </Button>
                <InfoTip text={help.drawerPlan} />
                </Stack>
                <Collapse in={planOpen} unmountOnExit>
                  <Stack spacing={0.5} sx={{ mt: 1, pl: 1.5, borderLeft: `2px solid ${launchColors.divider}` }}>
                    <Typography sx={{ fontSize: 13 }}>Ursprunglig grundplan: {formatDate(detail.baseline.originalDue)}</Typography>
                    <Typography sx={{ fontSize: 13 }}>Nuvarande grundplan: {formatDate(detail.baseline.currentDue)}</Typography>
                    <Typography sx={{ fontSize: 13 }}>Väntat datum: {detail.activity.forecastDue ? formatDate(detail.activity.forecastDue) : 'Inget'}</Typography>
                    {detail.activity.forecastSource && (
                      <Typography sx={{ fontSize: 12, color: launchColors.textSecondary }}>Källa: {detail.activity.forecastSource} · bedömd {formatDate(detail.activity.forecastAssessedAt)}</Typography>
                    )}
                    {detail.baseline.versions.map((version) => (
                      <Typography key={`${version.planVersion}-${version.committedAt}`} sx={{ fontSize: 12, color: launchColors.textSecondary }}>
                        Planversion {version.planVersion}: {formatDate(version.due)}{version.committedAt ? ` · fastställd ${formatDate(version.committedAt)}` : ''}
                      </Typography>
                    ))}
                  </Stack>
                </Collapse>
              </Box>

              <Box>
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
                  <Typography component="h3" sx={overlineSx}>Historik</Typography>
                  <InfoTip text={help.drawerHistory} />
                </Stack>
                {detail.history.length === 0 && <Typography sx={{ fontSize: 13, color: launchColors.textSecondary }}>Ingen aktivitetshistorik ännu.</Typography>}
                <Stack component="ol" spacing={0} sx={{ listStyle: 'none', m: 0, p: 0 }}>
                  {[...detail.history].reverse().map((event, index, events) => (
                    <Box component="li" key={`${event.commandId}-${index}`} sx={{ display: 'grid', gridTemplateColumns: '28px 1fr auto', columnGap: 1, alignItems: 'start' }}>
                      <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center', height: '100%' }}>
                        {index < events.length - 1 && <Box aria-hidden sx={{ position: 'absolute', top: 24, bottom: 0, width: '2px', bgcolor: launchColors.divider }} />}
                        <Box aria-hidden sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: launchColors.softNavy, color: launchColors.navy, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1 }}>
                          {historyIcon(event.type)}
                        </Box>
                      </Box>
                      <Box sx={{ pb: 1.5, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{eventTypeText(event.type)}</Typography>
                        {eventDetail(event) && <Typography sx={{ fontSize: 12, color: launchColors.textSecondary, overflowWrap: 'anywhere' }}>{eventDetail(event)}</Typography>}
                      </Box>
                      <Typography sx={{ fontSize: 12, color: launchColors.textSecondary, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{shortDate(event.actuallyOccurredAt, asOf)}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>

              <Box>
                <Stack direction="row" alignItems="center">
                <Button size="small" onClick={() => setTechnicalOpen((open) => !open)} aria-expanded={technicalOpen} endIcon={technicalOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />} sx={{ px: 0, color: launchColors.textSecondary }}>
                  Tekniska detaljer
                </Button>
                <InfoTip text={help.drawerTechnical} />
                </Stack>
                <Collapse in={technicalOpen} unmountOnExit>
                  <Stack spacing={0.25} sx={{ mt: 0.5, fontSize: 12, color: launchColors.textSecondary }}>
                    <Typography sx={{ fontSize: 12 }}>Källrad: {detail.source.sourceKey}</Typography>
                    <Typography sx={{ fontSize: 12 }}>Typ: {detail.source.kind} · omfattning: {detail.source.scope} · vikt: {detail.source.weight}</Typography>
                    <Typography sx={{ fontSize: 12 }}>Underlagsrevision: {detail.source.evidenceRevision}</Typography>
                    <Typography sx={{ fontSize: 12 }}>Beslutskrav: {detail.source.approvalPolicy}{detail.source.requiredRoles.length ? ` (${detail.source.requiredRoles.map(displayText).join(', ')})` : ''}</Typography>
                  </Stack>
                </Collapse>
              </Box>
            </Stack>
          )}
        </Box>
      </Drawer>
      <UploadFilesDialog
        open={uploadOpen}
        folders={detail?.fileFolders ?? []}
        saving={fileBusy}
        progress={uploadProgress}
        error={uploadError}
        rules={detail?.fileRules}
        onClose={() => setUploadOpen(false)}
        onSubmit={(files, folderId) => { void uploadAttachments(files, folderId); }}
      />
      <Snackbar open={Boolean(notice)} autoHideDuration={5000} onClose={() => setNotice(null)} message={notice || ''} />
    </>
  );
};

export default ActivityDetailDrawer;

function historyIcon(type: string): React.ReactNode {
  const sx = { fontSize: 14 };
  if (type === 'start') return <PlayArrowIcon sx={sx} />;
  if (type === 'complete') return <CheckIcon sx={sx} />;
  if (type === 'reopen') return <ReplayIcon sx={sx} />;
  if (type === 'assign') return <PersonOutlineIcon sx={sx} />;
  if (type === 'blocker' || type === 'resolveBlocker') return <BlockIcon sx={sx} />;
  if (type === 'decide') return <GavelIcon sx={sx} />;
  if (type === 'revise' || type === 'attachFile' || type === 'removeFile' || type === 'moveFile') return <DescriptionOutlinedIcon sx={sx} />;
  if (type === 'setForecast' || type === 'proposePlanChange' || type === 'commitPlanChange') return <EventOutlinedIcon sx={sx} />;
  return <EditOutlinedIcon sx={sx} />;
}

function safeHttpUrl(value: string | null | undefined): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    return ['http:', 'https:'].includes(url.protocol) ? url.toString() : null;
  } catch {
    return null;
  }
}
